import type { SearchModes } from '@/domainLogic/index.ts';
import { LeisureType } from '@/domainLogic/index.ts';
import { type SearchInfoDetailData, DESTINATION_TYPE } from '@/app/search/types.ts';
import type { DefaultDestItems } from '@/app/components/search/utils';
import getSearchParams from '../../common/getSearchParams';

const analyticsLeisureDestinationTypes = {
  [LeisureType.BEACH]: 'beach',
  [LeisureType.EXOTIC]: 'exotic',
  [LeisureType.SKI]: 'ski',
  [LeisureType.CITY_BREAKS]: 'citybreaks',
  [LeisureType.SPA]: 'spa',
};

const ANALYTICS_DESTINATION_TYPE: { [T in Exclude<DESTINATION_TYPE, DESTINATION_TYPE.LEISURE>]: string } = {
  [DESTINATION_TYPE.TRAIN_STATION]: 'trainstation',
  [DESTINATION_TYPE.AIRPORT]: 'airports',
  [DESTINATION_TYPE.ANYWHERE]: 'anywhere',
  [DESTINATION_TYPE.COUNTRY]: 'countries',
  [DESTINATION_TYPE.TAG]: 'tags',
  [DESTINATION_TYPE.CITY]: 'cities',
  [DESTINATION_TYPE.GEO]: 'geo',
};

const getDestinationType = (destination: string, destinationType?: DESTINATION_TYPE): string => {
  if (destinationType === DESTINATION_TYPE.LEISURE) return analyticsLeisureDestinationTypes[destination as LeisureType];
  if (!destinationType) return '';
  return ANALYTICS_DESTINATION_TYPE[destinationType];
};

const getDestinationTypeCategory = (
  destination: string,
  defaultDestItems: DefaultDestItems[],
  searchMode: SearchModes,
  isSearch?: boolean
): string => {
  const { source } = getSearchParams();
  const defaultValues =
    defaultDestItems
      .filter((x) => x.searchMode === searchMode)?.[0]
      ?.suggestions?.[0]?.suggestionItems?.map((y) => y.code.toString()) || [];

  if (defaultValues.indexOf(destination) !== -1) {
    return `recommendations_${defaultValues.indexOf(destination) + 1}`;
  }

  if (destination === 'A') return 'recommendations_anywhere';
  if (destination === LeisureType.CITY_BREAKS) return 'recommendations_citybreaks';
  if (destination === LeisureType.BEACH) return 'recommendations_beach';
  if (destination === LeisureType.EXOTIC) return 'recommendations_exotic';
  if (destination === LeisureType.SKI) return 'recommendations_ski';
  if (destination === LeisureType.SPA) return 'recommendations_spa';

  if (isSearch || !source) {
    return 'standard_search_results';
  }
  return 'prepopulated_data';
};

type GetDestinationData = {
  destination: string;
  defaultDestItems: DefaultDestItems[];
  isSearch: boolean;
  searchMode: SearchModes;
  searchInfoDetailData: SearchInfoDetailData;
};

const getDestinationData = async ({
  destination,
  defaultDestItems,
  isSearch,
  searchMode,
  searchInfoDetailData,
}: GetDestinationData) => {
  const destinationType = searchInfoDetailData?.destination?.destinationType;
  const destinationDescription = searchInfoDetailData?.destination?.description || '';

  const analyticsDestinationType = await getDestinationType(
    destination,
    searchInfoDetailData?.destination?.destinationType
  );
  const destinationTypeCategory = getDestinationTypeCategory(destination, defaultDestItems, searchMode, isSearch);
  let destinationName = searchInfoDetailData?.destination?.cityName || '';
  let destinationCode = destination || '';
  let destinationCountryCode = searchInfoDetailData?.destination?.countryCode || '';
  let destinationCountryName = searchInfoDetailData?.destination?.countryName || '';

  if (destinationType === DESTINATION_TYPE.COUNTRY) {
    destinationCode = destinationCountryCode;
    destinationName = destinationCountryName;
  } else if (destinationType === DESTINATION_TYPE.GEO) {
    destinationName = destinationDescription;
  } else if (destinationType === DESTINATION_TYPE.ANYWHERE) {
    destinationCode = 'Any';
    destinationName = 'Any';
    destinationCountryCode = '';
    destinationCountryName = '';
  }

  return {
    destinationType: analyticsDestinationType,
    destinationTypeCategory,
    destinationCode,
    destinationName,
    destinationCountryCode,
    destinationCountryName,
  };
};

export default getDestinationData;
